import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { useFormik } from 'formik';
import React from 'react';
import {
	Accordion as AccordionPackage,
	AccordionItem,
	AccordionItemButton,
	AccordionItemHeading,
	AccordionItemPanel,
} from 'react-accessible-accordion';
import { Detector } from 'react-detect-offline';
import { useHistory, useLocation } from 'react-router-dom';
import Slider from 'react-slick';
import Switch from 'react-switch';

import { getSearch } from '../../api/GET_search';
import { postPagination } from '../../api/POST_pagination';
import { postProductCount } from '../../api/POST_productCount';
import { postProductCounts } from '../../api/POST_productCounts';
import Header from '../../components/Header';
import Info from '../../components/Info';
import Layout from '../../components/Layout';
import ModalAccountInfo from '../../components/ModalAccountInfo';
import ModalConfirmation from '../../components/ModalConfirmation';
import Table from '../../components/Table';
import TableInput from '../../components/TableInput';
import { isMobile } from '../../utils/checkDimensions';
import {
	_removeLocalStorageItem,
	_retrieveLocalStorageItem,
	_storeLocalStorageItem,
} from '../../utils/localStorage';
import { convertCommaToDot } from '../../utils/numberFormatter';
import { settings } from '../../utils/staticData';
import {
	Brand,
	BrandStatus,
	Count,
	Line,
	Links,
	OutletCrewData,
	OutletCrewForm,
	Product,
	ProductCount,
	ProductCounts,
	Products,
} from './Home.type';

const columns = [
	{
		Header: 'Kode Produk',
		accessor: 'productCode',
		maxWidth: 120,
		minWidth: 60,
		width: 80,
	},
	{
		Header: 'Nama Produk',
		accessor: 'productName',
		maxWidth: 730,
		minWidth: 130,
		width: 150,
	},
	{
		Header: 'UoM',
		accessor: 'productUom',
		maxWidth: 120,
		minWidth: 40,
		width: 60,
	},
	{
		Header: 'Hasil Hitung',
		accessor: 'counted',
		maxWidth: 150,
		minWidth: 120,
		width: 130,
		Cell: TableInput,
	},
];

const Form = () => {
	const history = useHistory();
	const location = useLocation();
	const state = location.state;
	const newState = state as OutletCrewData;

	const { handleSubmit, setFieldValue, values } = useFormik<OutletCrewForm>({
		initialValues: {
			products: [],
			idScheduler: null,
			crewName: '',
			outletName: '',
			scheduleName: '',
			scheduleTime: '',
			startHour: null,
			listProduct: [],
			newListProduct: [],
			totalProduct: 0,
			currentInputCount: 0,
			selectedBrand: null,
			brandStatus: [],
			selectedCategory: 0,
			textSearch: '',
			isOnline: false,
			isValid: false,
			isExpired: false,
			isSwitchProductCode: false,
			isSwitchEmptyProduct: false,
			isModalConfirmationVisible: false,
			isModalAccountVisible: false,
			isLoading: false,
			isSuccess: false,
			isSearch: false,
			isInfoVisible: false,
			isStickyVisible: true,
			isLoadMoreVisible: true,
			isAutosubmit: false,
			errorMessage: '',
		},
		onSubmit: async (formValues) => {
			const productIds: Array<Count> = [];
			formValues.products.map((products) =>
				products.counts.map((item) => {
					if (item.counted) {
						productIds.push({
							...item,
							counted: convertCommaToDot(item.counted),
						});
					}
				}),
			);

			const body: ProductCounts = {
				scheduleName: formValues.scheduleName,
				productIds,
			};

			await postProductCounts(body)
				.then(({ status, messageDescription }) => {
					if (status === 'success') {
						setFieldValue('isLoading', false);
						setFieldValue('isModalVisible', false);
						setFieldValue('isSuccess', true);
						setFieldValue('isInfoVisible', true);
						_removeLocalStorageItem('products');
						history.push('/outlet-crew-success');
					} else {
						setFieldValue('isLoading', false);
						setFieldValue('isModalVisible', false);
						setFieldValue('isInfoVisible', true);
						setFieldValue('errorMessage', messageDescription);
					}
				})
				.catch(() => {
					setFieldValue(
						'errorMessage',
						'Gagal Submit. Silahkan coba submit kembali',
					);
					if (formValues.isExpired) {
						_removeLocalStorageItem('products');
					}
				});
		},
	});

	const toggleSticky = () =>
		setFieldValue('isStickyVisible', !values.isStickyVisible);
	const toggleSwitchProductCode = (value: boolean) =>
		setFieldValue('isSwitchProductCode', value);
	const toggleSwitchEmptyProduct = (value: boolean) =>
		setFieldValue('isSwitchEmptyProduct', value);
	const toggleModalConfirmationVisible = () =>
		setFieldValue(
			'isModalConfirmationVisible',
			!values.isModalConfirmationVisible,
		);
	const toggleModalAccountVisible = () =>
		setFieldValue('isModalAccountVisible', !values.isModalAccountVisible);
	const toggleSearch = () => setFieldValue('isSearch', !values.isSearch);

	React.useEffect(() => {
		if (!values.isSearch) {
			const existingProducts = _retrieveLocalStorageItem('products');

			if (existingProducts) {
				const existingProductParse = JSON.parse(existingProducts);
				if (
					existingProductParse.products &&
					existingProductParse.products.length > 0
				) {
					setFieldValue('products', existingProductParse.products);
				}
			}

			if (newState?.data) {
				const {
					crewName,
					outletName,
					scheduleName,
					startHour,
					timeToInput,
					lines,
					idScheduler,
					isAutosubmit,
				} = newState?.data[0];

				const brand = {
					brandName: lines[0].productBrandId,
					brandCount: lines[0].countProductBrand,
				};

				const brandStatus = lines.reduce(
					(newData: Array<BrandStatus>, products) => {
						return [
							...newData,
							{
								brandName: products.productBrandId,
								isCompleted: false,
							},
						];
					},
					[],
				);

				const totalProduct = lines.reduce((result, item) => {
					return (result = result + item.countProductBrand);
				}, 0);

				const splitHour = startHour?.split(':');
				const hour = parseInt(splitHour && splitHour[0]);
				const minute = parseInt(splitHour && splitHour[1]);

				const time = new Date();
				time.setHours(hour, minute);
				time.setSeconds(time.getSeconds() + 7200);
				setFieldValue('isAutosubmit', isAutosubmit);
				setFieldValue('idScheduler', idScheduler);
				setFieldValue('crewName', crewName);
				setFieldValue('outletName', outletName);
				setFieldValue('scheduleName', scheduleName);
				setFieldValue('scheduleTime', timeToInput);
				setFieldValue('startHour', time);
				setFieldValue('selectedBrand', brand);
				setFieldValue('listProduct', lines);
				setFieldValue('brandStatus', brandStatus);
				setFieldValue('totalProduct', totalProduct);
			} else {
				history.push('/outlet-crew');
			}
		}
	}, [history, newState?.data, setFieldValue, values.isSearch]);

	React.useEffect(() => {
		if (values.textSearch) {
			getSearch(values.idScheduler, values.textSearch).then(
				({ data, status }) => {
					if (status === 'success') {
						setFieldValue('listProduct', data[0].lines);
					}
				},
			);
		}
	}, [setFieldValue, values.idScheduler, values.products, values.textSearch]);

	React.useEffect(() => {
		if (!values.isSearch) {
			if (values.products.length > 0) {
				const currentInputCount = values.products.reduce((result, item) => {
					const filterCounts = item.counts.filter((item) => item.counted);
					result = result + filterCounts.length;
					return result;
				}, 0);

				if (currentInputCount) {
					setFieldValue('currentInputCount', currentInputCount);
				}

				const brandStatus = values.listProduct.reduce(
					(brandStatuses: Array<BrandStatus>, product) => {
						const brandCount = values.products
							.find((value) => value.brand === product.productBrandId)
							?.counts.filter((item) => item.counted).length;
						return [
							...brandStatuses,
							{
								brandName: product.productBrandId,
								isCompleted: brandCount === product.countProductBrand,
							},
						];
					},
					[],
				);

				setFieldValue('isValid', currentInputCount === values.totalProduct);
				setFieldValue('brandStatus', brandStatus);
			}
		}
	}, [
		setFieldValue,
		values.listProduct,
		values.products,
		values.totalProduct,
		values.isSearch,
	]);

	const addProductCount = (
		productId: number,
		count: string,
		idScheduler: number,
	) => {
		const body: ProductCount = {
			counted: count ? convertCommaToDot(count) : '0',
		};
		postProductCount(idScheduler, productId, body);
	};

	const onHandleChange = (
		productId: number,
		count: string,
		categoryName: string,
	) => {
		const productTmp: Array<Product> = JSON.parse(
			JSON.stringify(values.products),
		);
		const brandIndex = productTmp.findIndex(
			(product) => product.brand === values.selectedBrand.brandName,
		);
		const productIndex = productTmp[brandIndex]?.counts.findIndex(
			(product) => product.productId === productId,
		);
		const product = {
			brand: values.selectedBrand.brandName,
			counts: [
				{
					productId,
					counted: count && count.charAt(0) !== ',' ? count : undefined,
					categoryName,
				},
			],
		};
		const filterProduct = values.products.filter(
			(item) => item.brand === values.selectedBrand.brandName,
		);
		if (productIndex >= 0) {
			productTmp[brandIndex].counts[productIndex] = {
				productId,
				counted: count && count.charAt(0) !== ',' ? count : undefined,
				categoryName,
			};
			setFieldValue('products', productTmp);
			const storeProducts = {
				time: new Date(),
				products: productTmp,
			};
			const productsStringify = JSON.stringify(storeProducts);
			_storeLocalStorageItem({
				storageKey: 'products',
				storageValue: productsStringify,
			});
		} else {
			const updateCounts =
				filterProduct.length > 0
					? filterProduct.map((item) => {
							item.counts.push(...product.counts);
							return item;
					  })
					: [...values.products, product];
			const groupProductBySameBrand = Array.from(
				new Set([...values.products, ...updateCounts]),
			);
			const newProducts =
				values.products.length > 0
					? groupProductBySameBrand
					: [...values.products, product];
			setFieldValue('products', newProducts);
			const storeProducts = {
				time: new Date(),
				products: newProducts,
			};
			const newProductsStringify = JSON.stringify(storeProducts);
			_storeLocalStorageItem({
				storageKey: 'products',
				storageValue: newProductsStringify,
			});
		}
	};

	const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (!/^[0-9.,]*$/.test(event.key)) {
			event.preventDefault();
		}
	};

	const fetchPagination = (link: Links) => {
		postPagination(link?.nextPage, { scheduleName: values.scheduleName }).then(
			(res) => {
				if (res && res.status === 'success') {
					const tempListProduct = JSON.parse(
						JSON.stringify(values.listProduct),
					);
					const lines = res.data[0].lines;
					const brand = lines[0].productBrandId;
					const productCategory = lines[0].products[0].productCategory;
					const product = lines[0].products[0].product;
					const links = lines[0].products[0].links;
					const filterBrand = tempListProduct.filter(
						(item) => item.productBrandId === brand,
					);
					const filterCategory = filterBrand[0].products.filter(
						(item) => item.productCategory === productCategory,
					);

					filterCategory.map((item) => {
						item.links = links;
						item.product.push(...product);
						return item;
					});

					setFieldValue('listProduct', tempListProduct);
				}
			},
		);
	};

	const onClickBrand = (brand: Brand) => {
		setFieldValue('selectedBrand', brand);
	};

	React.useEffect(() => {
		const listProductFilter = values.listProduct.filter(
			(item) => item.productBrandId === values.selectedBrand.brandName,
		);
		const listProductTmp =
			listProductFilter.map((item) => item.products)[0] || [];
		const counts =
			values.products.length > 0 &&
			values.products
				.filter((item) => item.brand === values.selectedBrand.brandName)
				.map((item) => item.counts);

		const newListProduct: Array<Products> = listProductTmp.map((item) => {
			let product = item.product.map((product) => {
				const counted =
					counts[0]?.find((count) => count.productId === product.productId)
						?.counted || undefined;
				return { ...product, counted };
			});

			if (values.isSwitchEmptyProduct) {
				product = product.filter((product) => product.counted === undefined);
			}
			return {
				...item,
				product,
			};
		});

		setFieldValue('newListProduct', newListProduct);
	}, [
		setFieldValue,
		values.isSwitchEmptyProduct,
		values.listProduct,
		values.selectedBrand?.brandName,
	]);

	const listBrand = () =>
		values.listProduct.map((item, index) => {
			const brand = {
				brandName: item.productBrandId,
				brandCount: item.countProductBrand,
			};
			const isCompleted =
				values.brandStatus.find(
					(brandstatus) => brandstatus.brandName === brand.brandName,
				)?.isCompleted || false;

			return (
				<div key={index} className="mr-1">
					<button
						onClick={() => onClickBrand(brand)}
						className={
							isCompleted &&
							values.selectedBrand.brandName === item.productBrandId
								? 'tablet:h-7.5 mobile:h-7 bg-primary-green rounded flex flex-row items-center justify-center p-1 focus:outline-none focus:ring-transparent slick-custom'
								: !isCompleted &&
								  values.selectedBrand.brandName === item.productBrandId
								? 'tablet:h-7.5 mobile:h-7 bg-warning-default rounded flex flex-row items-center justify-center p-1 focus:outline-none focus:ring-transparent slick-custom'
								: 'tablet:h-7.5 mobile:h-7 bg-white border border-gray-gray300 rounded flex flex-row items-center justify-center p-1 focus:outline-none focus:ring-transparent slick-custom'
						}
					>
						<div
							className={
								isCompleted &&
								values.selectedBrand.brandName === item.productBrandId
									? 'text-white laptop:text-large tablet:text-large mobile:text-regular font-interRegular mr-2'
									: !isCompleted &&
									  values.selectedBrand.brandName === item.productBrandId
									? 'text-white laptop:text-large tablet:text-large mobile:text-regular font-interRegular mr-2'
									: isCompleted
									? 'text-primary-green laptop:text-large tablet:text-large mobile:text-regular font-interRegular mr-2'
									: 'text-warning-default laptop:text-large tablet:text-large mobile:text-regular font-interRegular mr-2'
							}
						>
							{item.productBrandId}
						</div>
						<div>
							{isCompleted &&
							values.selectedBrand.brandName === item.productBrandId ? (
								<img src="images/checkWhite.svg" />
							) : !isCompleted &&
							  values.selectedBrand.brandName === item.productBrandId ? (
								<img src="/images/warningWhite.svg" />
							) : isCompleted ? (
								<img src="images/check.svg" />
							) : (
								<img src="/images/warning.svg" />
							)}
						</div>
					</button>
				</div>
			);
		});

	const getCountFill = React.useCallback(
		(categoryName: string, product: Array<Line>) => {
			const products = values.products.find(
				(product) => product.brand === values.selectedBrand?.brandName,
			);

			const count =
				products?.counts.filter((count) =>
					values.isSearch && values.textSearch
						? product.find((item) => count.productId === item.productId) &&
						  count.categoryName === categoryName &&
						  count.counted
						: count.categoryName === categoryName && count.counted,
				).length || 0;
			return count;
		},
		[
			values.isSearch,
			values.products,
			values.selectedBrand?.brandName,
			values.textSearch,
		],
	);

	const categoryProduct = () => {
		const counts =
			values.products.length > 0 &&
			values.products
				.filter((item) => item.brand === values.selectedBrand.brandName)
				.map((item) => item.counts);

		return values.newListProduct.map((item, index) => {
			return (
				<AccordionItem uuid={index} key={index}>
					<AccordionItemHeading className="mb-2">
						<AccordionItemButton className="flex">
							<div className="flex justify-center items-center">
								<img
									src="images/arrowAccordion.svg"
									style={{
										transform:
											values.selectedCategory === index ? 'rotate(90deg)' : '',
										transition: '700ms',
										marginRight: 10,
									}}
									className="tablet:w-5 tablet:h-5 mobile:w-4 mobile:h-4"
								/>
								<div className="laptop:text-extraLarge tablet:text-large mobile:text-regular font-interMedium mr-2">{`${
									item.productCategory
								} (${getCountFill(item.productCategory, item.product)}/${
									item.countProduct
								})`}</div>
								{!values.isSearch &&
									getCountFill(item.productCategory, item.product) > 0 &&
									item.countProduct > 0 &&
									getCountFill(item.productCategory, item.product) ===
										item.countProduct && (
										<div className="w-3 h-3 bg-positive-default rounded-full">
											<img src="images/checkWhite.svg" />
										</div>
									)}
							</div>
						</AccordionItemButton>
					</AccordionItemHeading>
					<AccordionItemPanel>
						<Table
							columns={columns}
							counts={counts[0]}
							data={item.product}
							updateData={onHandleChange}
							addProductCount={addProductCount}
							idScheduler={values.idScheduler}
							categoryName={item.productCategory}
							onKeyPress={onKeyPress}
							isSuccess={values.isSuccess}
							type="outletcrew"
							isSwitchProductCode={values.isSwitchProductCode}
							isSwitchEmptyProduct={values.isSwitchEmptyProduct}
							countProduct={item.countProduct}
							fetchPagination={() => fetchPagination(item.links)}
						/>
					</AccordionItemPanel>
				</AccordionItem>
			);
		});
	};

	const cancelButtonRef = React.useRef(null);

	const closeModalBackground = () => undefined;

	const onClick = () => {
		setFieldValue('isLoading', true);
		handleSubmit();
	};

	const onChangeCategory = (value: Array<string>) =>
		setFieldValue('selectedCategory', value[0]);

	const onExpire = () => {
		if (values.isAutosubmit) {
			setFieldValue('isExpired', true);
			handleSubmit();
		} else {
			return;
		}
	};

	const checkNetwork = () => (
		<Detector
			render={({ online }) => {
				if (!online) {
					return (
						<div className="flex justify-center items-center bg-danger-default w-full laptop:h-10.5 tablet:h-10.5 mobile:h-9 laptop:mt-16 tablet:mt-16 mobile:mt-11 laptop:-mb-16 tablet:-mb-16 mobile:-mb-11">
							<div className="laptop:text-large tablet:text-large mobile:text-regular text-white font-interRegular">
								Tidak ada koneksi internet
							</div>
						</div>
					);
				} else {
					if (values.isOnline) {
						return (
							<div className="flex justify-center items-center bg-positive-lighter w-full laptop:h-10.5 tablet:h-10.5 mobile:h-9 laptop:mt-16 tablet:mt-16 mobile:mt-11 laptop:-mb-16 tablet:-mb-16 mobile:-mb-11">
								<div className="laptop:text-large tablet:text-large mobile:text-regular text-primary-green font-interRegular">
									Koneksi stabil, halaman akan direload dalam 3 detik
								</div>
							</div>
						);
					}
					return <div />;
				}
			}}
			onChange={(value) => {
				if (value) {
					setFieldValue('isOnline', true);
					setTimeout(() => window.location.reload(), 3000);
				}
			}}
		/>
	);

	const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFieldValue('textSearch', event.target.value);
	};

	const onCancel = () => {
		onRemoveText();
		toggleSearch();
	};

	const onRemoveText = () => {
		const { lines } = newState?.data[0];
		setFieldValue('textSearch', '');
		setFieldValue('listProduct', lines);
	};

	return (
		<div className="flex flex-col min-h-screen overflow-hidden">
			<ModalConfirmation
				icon="/images/warning.svg"
				title="Simpan Perhitungan Stok?"
				description="Apakah kamu yakin telah melakukan perhitungan dengan benar?"
				onClick={onClick}
				cancelButtonRef={cancelButtonRef}
				isShowModal={values.isModalConfirmationVisible}
				isLoading={values.isLoading}
				closeModal={toggleModalConfirmationVisible}
				closeModalBackground={closeModalBackground}
				variantButton="DOUBLE"
				textLeft="Batal"
				textRight="Simpan"
			/>
			<ModalAccountInfo
				nameOutlet={values.outletName}
				outletCrew={values.crewName}
				scheduleTime={values.scheduleTime}
				cancelButtonRef={cancelButtonRef}
				isShowModal={values.isModalAccountVisible}
				closeModal={toggleModalAccountVisible}
				closeModalBackground={closeModalBackground}
			/>
			<Header
				title="Outlet Stock Management"
				type="outletcrew"
				time={values.startHour}
				isExpired={values.isExpired}
				onExpire={onExpire}
			/>
			<Layout>
				<section className="relative laptop:pt-20 tablet:pt-20 mobile:pt-15 pb-10">
					{values.isStickyVisible && (
						<div className="flex justify-center">
							<div
								className={
									values.isInfoVisible
										? 'laptop:w-97 tablet:w-97 mobile:w-95% bg-gray-gray700 rounded-lg flex justify-between py-2 p-4 laptop:top-60 tablet:top-60 mobile:top-44 absolute z-30'
										: 'laptop:w-97 tablet:w-97 mobile:w-95% bg-gray-gray700 rounded-lg flex justify-between py-2 p-4 laptop:top-44 tablet:top-44 mobile:top-32 absolute z-30'
								}
							>
								<div className="tablet:text-large mobile:text-regular text-white font-interRegular">
									Kamu harus menginputkan semua hasil hitung produk di bawah ini
									minimal 0
								</div>
								<img
									onClick={toggleSticky}
									src="/images/close.svg"
									alt="close"
								/>
							</div>
						</div>
					)}

					<div className="w-full fixed z-40 bg-white drop-shadow-x tablet:h-15.5 mobile:h-11 laptop:mb-6 tablet:mb-5 tablet:py-4 mobile:py-2">
						<div className="absolute z-50 flex justify-center items-center laptop:mx-36 tablet:mx-8 mobile:mx-2 tablet:my-1 mobile:my-1">
							<div
								className="flex justify-center items-center"
								onClick={toggleModalAccountVisible}
							>
								<img
									src="/images/infoGreen.svg"
									className="tablet:w-5.5 tablet:h-5.5 mobile:w-3 mobile:h-3"
								/>
								<div className="tablet:text-large mobile:text-regular text-primary-green font-interRegular capitalize ml-1">
									info
								</div>
							</div>
							<div className="border-r border-gray-gray300 border-solid h-4 mx-2.5" />
						</div>
						<div className="laptop:pl-54 tablet:pl-28 mobile:pl-18 min-w-0 min-h-0">
							<Slider {...settings}>{listBrand()}</Slider>
						</div>
					</div>
					{values.isInfoVisible && (
						<div className="laptop:mb-6 tablet:mb-5 mobile:mb-3 tablet:mt-20 mobile:mt-14 laptop:mx-36 tablet:mx-10 mobile:mx-4">
							<Info
								variant={values.isSuccess ? 'positive' : 'warning'}
								text={
									values.isSuccess
										? 'Kamu telah melakukan perhitungan stok'
										: values.errorMessage
								}
							/>
						</div>
					)}
					{checkNetwork()}
					<div
						className={
							values.isInfoVisible
								? 'bg-white rounded-xl max-w-full laptop:p-5 tablet:p-5 mobile:p-2.5 laptop:mb-6 tablet:mb-6 mobile:mb-4 laptop:mx-36 tablet:mx-8 mobile:mx-2'
								: 'bg-white rounded-xl max-w-full laptop:p-5 tablet:p-5 mobile:p-2.5 laptop:mt-20 tablet:mt-20 mobile:mt-15 laptop:mb-6 tablet:mb-6 mobile:mb-4 laptop:mx-36 tablet:mx-8 mobile:mx-2'
						}
					>
						<div className="flex justify-between tablet:mb-7 mobile:mb-5">
							<div className="flex items-center">
								<div className="flex items-center">
									<div className="tablet:w-full mobile:w-20 text-gray-gray700 laptop:text-large tablet:text-large mobile:text-small font-interRegular mr-2">
										Tampilkan kode produk
									</div>
									<Switch
										onChange={toggleSwitchProductCode}
										checked={values.isSwitchProductCode}
										uncheckedIcon={false}
										checkedIcon={false}
										onColor="#ebebeb"
										onHandleColor="#0B6732"
										offColor="#ebebeb"
										offHandleColor="#9e9e9e"
										activeBoxShadow="0px 0px 0px 0px transparent"
										width={isMobile ? 28 : 36}
										height={isMobile ? 13 : 16}
										handleDiameter={isMobile ? 16 : 20}
									/>
								</div>
								<div className="border-r border-gray-gray300 border-solid h-4 mx-2.5" />
								<div className="flex items-center">
									<div className="tablet:w-full mobile:w-24 text-gray-gray700 laptop:text-large tablet:text-large mobile:text-small font-interRegular mr-2">
										Tampilkan produk kosong
									</div>
									<Switch
										onChange={toggleSwitchEmptyProduct}
										checked={values.isSwitchEmptyProduct}
										uncheckedIcon={false}
										checkedIcon={false}
										onColor="#ebebeb"
										onHandleColor="#0B6732"
										offColor="#ebebeb"
										offHandleColor="#9e9e9e"
										activeBoxShadow="0px 0px 0px 0px transparent"
										width={isMobile ? 28 : 36}
										height={isMobile ? 13 : 16}
										handleDiameter={isMobile ? 16 : 20}
									/>
								</div>
							</div>
							<button
								onClick={toggleSearch}
								className="flex justify-center items-center w-9 h-9 bg-primary-green rounded-lg"
							>
								<img src="/images/searchWhite.svg" />
							</button>
						</div>
						{values.isSearch && (
							<div className="relative">
								<div className="w-full bg-white flex justify-center items-center laptop:-mt-18 tablet:-mt-18 mobile:-mt-14 z-20 absolute">
									<div className="w-full mr-3">
										<input
											autoFocus
											value={values.textSearch}
											onChange={onSearch}
											placeholder="Cari nama produk"
											className="w-full flex justify-center items-center bg-white border border-gray-gray500 rounded-lg laptop:py-3 tablet:py-3 mobile:py-2 px-9 border focus:border-2 focus:border-gray-gray700 text-gray-gray700 laptop:text-large tablet:text-large mobile:text-regular outline-none font-interRegular"
										/>
										<img
											src="/images/searchBlack.svg"
											className="absolute mr-2 laptop:-mt-9 tablet:-mt-9 mobile:-mt-7 ml-3"
										/>
										{values.textSearch && (
											<div
												onClick={onRemoveText}
												className="w-full absolute laptop:-ml-18 laptop:-mt-9 tablet:-ml-18 tablet:-mt-9 mobile:-ml-14 mobile:-mt-8 flex justify-end items-end"
											>
												<img src="images/cross.svg" />
											</div>
										)}
									</div>
									<div
										onClick={onCancel}
										className="text-primary-green laptop:text-large tablet:text-large mobile:text-regular font-interSemiBold cursor-pointer"
									>
										Batal
									</div>
								</div>
							</div>
						)}
						<AccordionPackage onChange={onChangeCategory} preExpanded={[0]}>
							{categoryProduct()}
						</AccordionPackage>
					</div>
					{!values.isSuccess && (
						<button
							disabled={!values.isValid}
							onClick={toggleModalConfirmationVisible}
							className={
								values.isValid
									? 'laptop:w-80 tablet:w-80 mobile:w-96% laptop:h-14 tablet:h-13.5 mobile:h-12 bg-primary-green rounded-lg flex items-center justify-center laptop:mx-36 tablet:mx-8 mobile:mx-2'
									: 'laptop:w-80 tablet:w-80 mobile:w-96% laptop:h-14 tablet:h-13.5 mobile:h-12 bg-gray-gray300 rounded-lg flex items-center justify-center laptop:mx-36 tablet:mx-8 mobile:mx-2'
							}
						>
							<div
								className={
									values.isValid
										? 'text-white laptop:text-large tablet:text-large mobile:text-regular font-interSemiBold'
										: 'text-gray-gray500 laptop:text-large tablet:text-large mobile:text-regular font-interSemiBold'
								}
							>
								{values.isValid
									? 'Submit'
									: `${values.currentInputCount} dari ${values.totalProduct} produk telah di hitung`}
							</div>
						</button>
					)}
				</section>
			</Layout>
		</div>
	);
};

export default Form;
